import { CSSProperties } from 'react';

type Styles = Record<string, CSSProperties>;

/**
 * Amanah Mini App dizayn tokenlari — rang, shisha (glass), radius va soyaning
 * yagona manbasi. Ekranlar tokendan oladi, o'zi rang o'ylab topmaydi.
 *
 * Yo'nalish: "Amanah + zamonaviy iOS". Shisha effekt faqat qatlam va
 * navigatsiyani ajratish uchun; biznes ma'lumot (jadval, summa, ro'yxat)
 * doim qattiq oq yuzada va o'qish uchun eng yuqori kontrastda qoladi.
 */
export const T = {
  // ── Brend ────────────────────────────────────────────────────────────────
  brand: '#34C759',
  brandHover: '#2DB84D',
  brandActive: '#28A745',
  brandTint: '#E9F9EE',
  brandGlass08: 'rgba(52,199,89,0.08)',
  brandGlass12: 'rgba(52,199,89,0.12)',
  brandGlass18: 'rgba(52,199,89,0.18)',

  // ── Matn ─────────────────────────────────────────────────────────────────
  ink: '#1C1C1E',
  inkSub: '#6E6E73',   // ikkilamchi matn — oq ustida ~4.6:1 (AA)
  inkFaint: '#8E8E93', // faqat eng mayda izohlar uchun

  // ── Semantik ─────────────────────────────────────────────────────────────
  danger: '#FF3B30',
  dangerTint: 'rgba(255,59,48,0.08)',
  warn: '#FF9500',
  warnTint: 'rgba(255,149,0,0.10)',

  // ── Yuza ─────────────────────────────────────────────────────────────────
  bg: '#F3F6F4',       // juda yengil, sal yashil tomon og'gan neytral fon
  surface: '#FFFFFF',
  line: '#E5E5EA',
  hairline: 'rgba(0,0,0,0.06)',

  // ── Shisha ───────────────────────────────────────────────────────────────
  glass: 'rgba(255,255,255,0.72)',
  glassStrong: 'rgba(255,255,255,0.85)',
  glassBorder: 'rgba(255,255,255,0.55)',
  blur: 'saturate(180%) blur(20px)',
  blurStrong: 'saturate(180%) blur(30px)',

  // ── Radius ───────────────────────────────────────────────────────────────
  r10: 10, r12: 12, r14: 14, r18: 18, r22: 22, r26: 26,

  // ── Soya (past opacity, katta blur — chuqurlik, "photoshop" emas) ────────
  shadowCard: '0 1px 2px rgba(16,24,20,0.05)',
  shadowRaised: '0 2px 10px rgba(16,24,20,0.06)',
  shadowFloat: '0 8px 30px rgba(0,0,0,0.08)',
  shadowBrand: '0 10px 28px rgba(52,199,89,0.30)',
  shadowSheet: '0 -10px 40px rgba(0,0,0,0.14)',
} as const;

/** Suzuvchi yuqori panel balandligi — kontent shuning ostidan siljiydi. */
export const TOPBAR_H = 52;

/** Shisha yuza: blur + Safari/Telegram WebView uchun -webkit prefiksi. */
export const glass = (background: string, filter: string = T.blur): CSSProperties => ({
  background,
  backdropFilter: filter,
  WebkitBackdropFilter: filter,
});

export const miniStyles: Styles = {
  // ── Karkas ───────────────────────────────────────────────────────────────
  container: {
    flex: 1, minHeight: 0, position: 'relative', display: 'flex', flexDirection: 'column',
    background: T.bg, fontFamily: "-apple-system,'SF Pro Text','Inter','Segoe UI',sans-serif",
    color: T.ink, overflow: 'hidden',
  },

  /** Suzuvchi shisha yuqori panel — kontent uning ostidan o'tadi. */
  topBar: {
    position: 'absolute', top: 0, left: 0, right: 0, height: TOPBAR_H, zIndex: 60,
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    padding: '0 14px', boxSizing: 'border-box',
    ...glass(T.glassStrong, T.blurStrong),
    borderBottom: `0.5px solid ${T.hairline}`,
  },

  /** Yuqori paneldagi ekran almashtirgich — yengil, suzuvchi pill. */
  navBtn: {
    minWidth: 34, height: 32, borderRadius: T.r10, border: `0.5px solid ${T.hairline}`,
    background: 'rgba(120,120,128,0.08)', color: T.inkSub,
    fontSize: 14, fontWeight: 700, lineHeight: 1, cursor: 'pointer',
    display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0 9px',
  },
  navBtnActive: {
    background: T.brandGlass12, borderColor: T.brandGlass18, color: T.brandActive,
  },
  /** Til tugmasi — navBtn bilan bir xil, eski nom saqlangan. */
  langBtn: {
    minWidth: 34, height: 32, borderRadius: T.r10, border: `0.5px solid ${T.hairline}`,
    background: 'rgba(120,120,128,0.08)', color: T.inkSub,
    fontSize: 13, fontWeight: 700, lineHeight: 1, cursor: 'pointer',
    display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0 9px',
  },

  /** Ekran ildizi — yuqori panel absolyut, shuning uchun kontent 0 dan boshlanadi. */
  screen: { flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' },
  /** O'z sarlavhasi bor ekranlar uchun — panel ostidan boshlanadi. */
  screenInset: {
    flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden',
    paddingTop: TOPBAR_H,
  },

  /** Ichki sarlavha (orqaga tugmasi bor qator). */
  header: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    padding: '10px 12px', ...glass(T.glassStrong, T.blur),
    borderBottom: `0.5px solid ${T.hairline}`,
  },
  headerTitle: { fontWeight: 700, fontSize: 16, color: T.ink },
  backBtn: {
    background: 'none', border: 'none', cursor: 'pointer', padding: 4, color: T.brandActive,
    display: 'flex', alignItems: 'center',
  },
  addNewBtn: {
    background: T.brandGlass12, border: `0.5px solid ${T.brandGlass18}`, borderRadius: T.r10,
    padding: '6px 11px', fontSize: 13, fontWeight: 600, color: T.brandActive, cursor: 'pointer',
  },

  // ── Qidiruv (ro'yxat ustida yopishib turadigan shisha qatlam) ────────────
  searchWrap: {
    // `top: 0` — skroll konteynerining padding qutisidan o'lchanadi, ya'ni
    // qidiruv aynan shisha yuqori panel ostiga yopishadi.
    position: 'sticky', top: 0, zIndex: 20,
    display: 'flex', alignItems: 'center', gap: 8,
    padding: '10px 14px',
    ...glass(T.glass, T.blur),
    borderBottom: `0.5px solid ${T.hairline}`,
  },
  searchInput: {
    flex: 1, border: 'none', outline: 'none', fontSize: 15, background: 'transparent',
    padding: '6px 0', color: T.ink,
  },
  clearBtn: { background: 'none', border: 'none', color: T.inkSub, fontSize: 16, cursor: 'pointer', padding: '0 4px' },

  // ── Ro'yxat ──────────────────────────────────────────────────────────────
  /** Skroll konteyneri — kontent yuqori panel ostidan siljishi uchun. */
  scrollArea: { flex: 1, minHeight: 0, overflowY: 'scroll', WebkitOverflowScrolling: 'touch' },
  /** Skroll ichidagi kontent qatlami. */
  listPad: { padding: '10px 12px 104px' },
  productList: { flex: 1, minHeight: 0, overflowY: 'scroll', padding: '10px 12px 104px' },

  // ── Ma'lumot kartalari — qasddan qattiq oq, o'qishga qulay ───────────────
  productCard: {
    background: T.surface, borderRadius: T.r18, padding: '11px 14px', marginBottom: 8,
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    border: `0.5px solid ${T.hairline}`, boxShadow: T.shadowCard,
    transition: 'background 0.18s ease, box-shadow 0.18s ease',
  },
  productInfo: { flex: 1, minWidth: 0 },
  productName: { fontWeight: 600, fontSize: 15, color: T.ink, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
  productMeta: { display: 'flex', gap: 10, marginTop: 3, alignItems: 'baseline' },
  productPrice: { fontSize: 14, fontWeight: 700, color: T.brandActive },
  productStock: { fontSize: 12, color: T.inkSub },

  qtyControls: { display: 'flex', alignItems: 'center', gap: 4, marginLeft: 10, flexShrink: 0 },
  qtyBtn: {
    width: 32, height: 32, borderRadius: T.r10, border: `1px solid ${T.line}`, background: T.surface,
    fontSize: 18, fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center',
    justifyContent: 'center', color: T.ink,
  },
  qtyInput: {
    width: 44, height: 32, borderRadius: T.r10, border: `1px solid ${T.line}`, textAlign: 'center',
    fontSize: 15, fontWeight: 700, outline: 'none', color: T.ink, background: T.surface,
  },
  addBtn: {
    width: 36, height: 36, borderRadius: T.r12, border: 'none',
    background: T.brand, color: '#fff', fontSize: 22, fontWeight: 600, cursor: 'pointer',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.28), 0 4px 12px rgba(52,199,89,0.28)',
  },

  // ── Suzuvchi savat paneli ────────────────────────────────────────────────
  cartFooter: {
    position: 'fixed', bottom: 'calc(12px + env(safe-area-inset-bottom, 0px))', left: 12, right: 12,
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    padding: '14px 18px', borderRadius: T.r22, cursor: 'pointer', zIndex: 50,
    background: `linear-gradient(180deg, ${T.brand} 0%, ${T.brandHover} 100%)`,
    border: '0.5px solid rgba(255,255,255,0.35)',
    boxShadow: `inset 0 1px 0 rgba(255,255,255,0.30), ${T.shadowBrand}`,
  },
  cartBadge: {
    background: 'rgba(255,255,255,0.92)', color: T.brandActive, fontWeight: 800, fontSize: 13,
    borderRadius: T.r10, padding: '2px 9px', minWidth: 22, textAlign: 'center',
  },

  // ── Qalqib chiquvchi xabar ───────────────────────────────────────────────
  errorToast: {
    position: 'fixed', top: TOPBAR_H + 12, left: '50%', transform: 'translateX(-50%)',
    background: T.danger, color: '#fff', padding: '9px 16px', borderRadius: T.r12,
    fontSize: 13, fontWeight: 600, zIndex: 99, maxWidth: '84%',
    boxShadow: '0 8px 30px rgba(255,59,48,0.30)',
  },
  noResults: { textAlign: 'center', color: T.inkSub, padding: 40, fontSize: 15 },

  // ── Forma ────────────────────────────────────────────────────────────────
  addForm: { padding: 12, background: T.bg, display: 'flex', flexDirection: 'column', gap: 8, borderBottom: `0.5px solid ${T.hairline}` },
  formInput: {
    padding: '11px 12px', borderRadius: T.r12, border: `1px solid ${T.line}`, fontSize: 15,
    outline: 'none', background: T.surface, color: T.ink,
  },
  confirmBtn: {
    padding: '11px 14px', borderRadius: T.r12, border: 'none', background: T.brand, color: '#fff',
    fontWeight: 700, fontSize: 15, cursor: 'pointer',
    boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.24), 0 4px 14px rgba(52,199,89,0.26)',
  },
  confirmBtnBig: {
    width: '100%', padding: 15, borderRadius: T.r18, border: 'none',
    background: `linear-gradient(180deg, ${T.brand} 0%, ${T.brandHover} 100%)`,
    color: '#fff', fontSize: 16, fontWeight: 700, cursor: 'pointer',
    boxShadow: `inset 0 1px 0 rgba(255,255,255,0.28), ${T.shadowBrand}`,
  },
  /** Ikkilamchi harakat — brend rangi, lekin to'ldirilmagan. */
  ghostBtn: {
    padding: '11px 14px', borderRadius: T.r12, background: T.surface, color: T.brandActive,
    border: `1px solid ${T.brandGlass18}`, fontWeight: 700, fontSize: 15, cursor: 'pointer',
    boxShadow: T.shadowCard,
  },

  // ── Mijoz ────────────────────────────────────────────────────────────────
  customerCard: {
    display: 'flex', alignItems: 'center', gap: 12, background: T.surface, borderRadius: T.r18,
    padding: '12px 14px', marginBottom: 8, cursor: 'pointer',
    border: `0.5px solid ${T.hairline}`, boxShadow: T.shadowCard,
  },
  customerAvatar: {
    width: 40, height: 40, borderRadius: '50%', background: T.brandGlass12, color: T.brandActive,
    fontWeight: 800, fontSize: 18, display: 'flex', alignItems: 'center', justifyContent: 'center',
    flexShrink: 0,
  },
  debtBadge: {
    background: T.dangerTint, color: T.danger, fontSize: 12, fontWeight: 700,
    padding: '4px 9px', borderRadius: T.r10, whiteSpace: 'nowrap',
  },

  // ── To'lov bloklari ──────────────────────────────────────────────────────
  paySection: {
    background: T.surface, borderRadius: T.r18, padding: 14, marginBottom: 10,
    border: `0.5px solid ${T.hairline}`, boxShadow: T.shadowCard,
  },
  /** Yuqori darajadagi yakuniy ko'rsatkich kartasi — yengil shisha. */
  glassCard: {
    ...glass(T.glass, T.blur),
    borderRadius: T.r18, padding: 14, marginBottom: 10,
    border: `1px solid ${T.glassBorder}`, boxShadow: T.shadowFloat,
  },
  /** Brend tusidagi shisha karta — yakuniy hisob-kitob uchun. */
  glassCardBrand: {
    ...glass('rgba(233,249,238,0.75)', T.blur),
    borderRadius: T.r18, padding: 14, marginBottom: 10,
    border: `1px solid ${T.brandGlass18}`, boxShadow: T.shadowFloat,
  },
  summaryRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '5px 0', fontSize: 14, color: T.ink },

  methodBtn: {
    flex: 1, padding: 11, borderRadius: T.r12, border: `1px solid ${T.line}`, background: T.surface,
    fontSize: 14, fontWeight: 600, cursor: 'pointer', textAlign: 'center', color: T.ink,
  },
  methodActive: { borderColor: T.brandGlass18, background: T.brandGlass12, color: T.brandActive },
  modeBtn: {
    flex: 1, padding: '9px 4px', borderRadius: T.r12, border: `1px solid ${T.line}`, background: T.surface,
    fontSize: 12, fontWeight: 600, cursor: 'pointer', textAlign: 'center', color: T.ink,
  },
  modeActive: {
    borderColor: 'transparent', color: '#fff',
    background: `linear-gradient(180deg, ${T.brand} 0%, ${T.brandHover} 100%)`,
    boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.24), 0 3px 10px rgba(52,199,89,0.24)',
  },
  amountInput: {
    width: '100%', padding: 13, borderRadius: T.r12, border: `1.5px solid ${T.brandGlass18}`,
    background: T.brandTint, fontSize: 22, fontWeight: 700, textAlign: 'center', outline: 'none',
    boxSizing: 'border-box', color: T.ink,
  },

  // ── Pastki varaq (bottom sheet) ──────────────────────────────────────────
  sheetScrim: {
    position: 'fixed', inset: 0, zIndex: 200, display: 'flex', alignItems: 'flex-end',
    // Ortidagi ekran biroz qorayadi, lekin varaqning o'zi oq bo'lib qolsin.
    ...glass('rgba(0,0,0,0.30)', 'blur(3px)'),
  },
  sheet: {
    width: '100%', maxHeight: '85vh', overflowY: 'auto', boxSizing: 'border-box',
    padding: '14px 16px calc(24px + env(safe-area-inset-bottom, 0px))',
    borderRadius: `${T.r26}px ${T.r26}px 0 0`,
    ...glass('rgba(255,255,255,0.90)', T.blurStrong),
    borderTop: `1px solid ${T.glassBorder}`, boxShadow: T.shadowSheet,
  },
  sheetHandle: { width: 36, height: 4, background: 'rgba(60,60,67,0.22)', borderRadius: 4, margin: '0 auto 14px' },
  sheetRow: {
    display: 'flex', alignItems: 'center', gap: 12, width: '100%', textAlign: 'left',
    background: T.surface, border: `0.5px solid ${T.hairline}`, borderRadius: T.r14,
    padding: '12px 14px', cursor: 'pointer', boxShadow: T.shadowCard,
  },

  receiptCard: { background: T.surface, borderRadius: T.r18, padding: 14, border: `0.5px solid ${T.hairline}`, boxShadow: T.shadowCard },
};
